import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getBuybackDashboard, getBuybackPurchaseHistory } from '@/services/api';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  Loader2, RefreshCw, TrendingUp, Package, Euro, Calendar,
  ShoppingCart, AlertOctagon, ChevronDown, ChevronUp, History, Zap
} from 'lucide-react';

const STATUS_LABELS = {
  TO_SEND: 'A expedier', SENT: 'Expedie', RECEIVED: 'Recu',
  PAID: 'Valide', MONEY_TRANSFERED: 'Paye', SUSPENDED: 'Suspendu',
  CANCELED: 'Annule', VALIDATED: 'Valide', COUNTER_PROPOSAL: 'Contre-offre',
};

const PIE_COLORS = ['#10b981', '#6366f1', '#06b6d4', '#f59e0b', '#a855f7', '#ef4444', '#f97316', '#3b82f6', '#8b5cf6'];
const formatEur = (v) => `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} EUR`;

const CustomBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      <p className="text-emerald-600">{payload[0]?.value} commandes</p>
    </div>
  );
};

const DAY_NAMES = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function formatDateFr(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = DAY_NAMES[d.getDay()];
  return `${day} ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' })}`;
}

export default function BuyBackDashboardTab() {
  const [dashboard, setDashboard] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setHistoryLoading(true);
    try {
      const [dash, hist] = await Promise.all([getBuybackDashboard(), getBuybackPurchaseHistory()]);
      setDashboard(dash);
      setHistory(hist);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); setHistoryLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        <span className="ml-3 text-slate-500">Chargement du tableau de bord...</span>
      </div>
    );
  }

  const today = history?.today || { count: 0, value: 0, limit_reached: false, active_listings: 0 };
  const dailyHistory = history?.daily_history || [];

  const chartData = (dashboard?.daily_chart || []).map(d => ({
    ...d,
    label: new Date(d.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  }));

  const pieData = Object.entries(dashboard?.status_distribution || {}).map(([key, val]) => ({
    name: STATUS_LABELS[key] || key, value: val
  }));

  return (
    <div className="space-y-5" data-testid="buyback-dashboard-tab">
      {/* LIMIT ALERT */}
      {today.limit_reached && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 flex items-center gap-4 animate-pulse" data-testid="limit-alert">
          <div className="bg-red-100 rounded-full p-3">
            <AlertOctagon className="w-8 h-8 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-red-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Limite d'achat atteinte</h3>
            <p className="text-sm text-red-600">
              Toutes vos annonces sont deconnectees. Vous avez achete <strong>{today.count} appareil{today.count > 1 ? 's' : ''}</strong> aujourd'hui pour <strong>{formatEur(today.value)}</strong>.
            </p>
            <p className="text-xs text-red-500 mt-1">{today.active_listings} annonces actives actuellement</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
            {dashboard ? `${dashboard.period_orders} commandes sur la periode · ${formatEur(dashboard.period_value)}` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="refresh-dashboard-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualiser
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dashboard-kpis">
        <KpiCard icon={ShoppingCart} label="Achats du jour" value={today.count}
          sub={today.limit_reached ? 'LIMITE ATTEINTE' : `appareil${today.count > 1 ? 's' : ''}`}
          color={today.limit_reached ? 'red' : 'emerald'} testId="kpi-today-count" />
        <KpiCard icon={Euro} label="Valeur du jour" value={formatEur(today.value)} sub="total" color="emerald" testId="kpi-today-value" />
        <KpiCard icon={Zap} label="Annonces actives" value={today.active_listings}
          sub={today.active_listings === 0 ? 'Toutes deconnectees' : 'en ligne'}
          color={today.active_listings === 0 ? 'red' : 'slate'} testId="kpi-active-listings" />
        <KpiCard icon={Package} label="Total commandes" value={(dashboard?.total_orders || 0).toLocaleString('fr-FR')} sub="historique" color="slate" testId="kpi-total" />
        <KpiCard icon={TrendingUp} label="Max journalier" value={history?.max_daily || '-'}
          sub="record sur 3 mois" color="indigo" testId="kpi-max-daily" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2" data-testid="daily-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Calendar className="w-4 h-4 text-emerald-500" />Volume journalier (14 jours)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={30} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} name="Commandes" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="status-pie-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Repartition statuts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val, name) => [`${val}`, name]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Purchase History */}
      <Card className="border-slate-200" data-testid="purchase-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <History className="w-4 h-4 text-indigo-500" />
            Historique des achats ({history?.total_days || 0} jours · {history?.total_orders_period || 0} commandes)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <span className="ml-2 text-sm text-slate-500">Chargement historique 3 mois...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="history-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50 text-left">
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 w-8"></th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500">Date</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-right">Appareils</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-right">Valeur</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500">Marches</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-center">Limite</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyHistory.map((day) => {
                    const isExp = expandedDay === day.date;
                    return (
                      <DayRow key={day.date} day={day} isExpanded={isExp}
                        onToggle={() => setExpandedDay(isExp ? null : day.date)}
                        maxDaily={history?.max_daily || 0} />
                    );
                  })}
                </tbody>
              </table>
              {dailyHistory.length === 0 && (
                <div className="text-center py-8 text-slate-500 text-sm">Aucune donnee</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Ranking */}
      {dashboard?.model_ranking?.length > 0 && (
        <Card className="border-slate-200" data-testid="model-ranking-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              Classement par modele ({dashboard.model_ranking.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="model-ranking-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/50 text-left">
                    <th className="py-2 px-4 text-xs font-medium text-slate-500">#</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500">Modele</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-right">Volume</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-right">Valeur</th>
                    <th className="py-2 px-4 text-xs font-medium text-slate-500 text-right">Prix moyen</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.model_ranking.map((m, i) => (
                    <tr key={m.model} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                      <td className="py-2 px-4 text-xs text-slate-400 font-medium">{i + 1}</td>
                      <td className="py-2 px-4 text-xs font-medium text-slate-800">{m.model}</td>
                      <td className="py-2 px-4 text-right font-semibold text-sm text-slate-900">{m.count}</td>
                      <td className="py-2 px-4 text-right text-xs text-slate-600">{formatEur(m.value)}</td>
                      <td className="py-2 px-4 text-right text-xs font-medium text-emerald-700">{m.avg_price} EUR</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DayRow({ day, isExpanded, onToggle, maxDaily }) {
  const isToday = day.is_today;
  const limitReached = day.limit_reached === true;
  const limitUnknown = day.limit_reached === null;
  // Visual: highlight if count is close to max (within 20%)
  const isHighVolume = maxDaily > 0 && day.count >= maxDaily * 0.8;
  const barWidth = maxDaily > 0 ? Math.min(100, Math.round((day.count / maxDaily) * 100)) : 0;

  return (
    <>
      <tr className={`border-b border-slate-100 cursor-pointer transition-colors
        ${isToday ? 'bg-emerald-50/50' : limitReached ? 'bg-red-50/30' : 'hover:bg-slate-50/80'}`}
        onClick={onToggle} data-testid={`day-row-${day.date}`}>
        <td className="py-2 px-4">
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </td>
        <td className="py-2 px-4">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${isToday ? 'text-emerald-700' : 'text-slate-700'}`}>
              {isToday ? "Aujourd'hui" : formatDateFr(day.date)}
            </span>
            {isToday && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">En cours</Badge>}
          </div>
        </td>
        <td className="py-2 px-4 text-right">
          <div className="flex items-center justify-end gap-2">
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden lg:block">
              <div className={`h-full rounded-full ${limitReached ? 'bg-red-400' : isHighVolume ? 'bg-amber-400' : 'bg-emerald-400'}`}
                style={{ width: `${barWidth}%` }} />
            </div>
            <span className={`font-bold text-sm ${limitReached ? 'text-red-700' : 'text-slate-900'}`}>{day.count}</span>
          </div>
        </td>
        <td className="py-2 px-4 text-right text-xs text-slate-600">{formatEur(day.value)}</td>
        <td className="py-2 px-4">
          <div className="flex gap-1">
            {day.markets && Object.entries(day.markets).map(([m, c]) => (
              <Badge key={m} className={`border-0 text-[10px] ${m === 'FR' ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'}`}>
                {m} {c}
              </Badge>
            ))}
          </div>
        </td>
        <td className="py-2 px-4 text-center">
          {limitReached ? (
            <Badge className="bg-red-100 text-red-700 border-0 text-[10px] gap-1" data-testid={`limit-reached-${day.date}`}>
              <AlertOctagon className="w-3 h-3" />Atteinte
            </Badge>
          ) : limitUnknown ? (
            isHighVolume ? (
              <Badge className="bg-amber-100 text-amber-600 border-0 text-[10px]">Probable</Badge>
            ) : (
              <span className="text-slate-300 text-[10px]">-</span>
            )
          ) : (
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">OK</Badge>
          )}
        </td>
      </tr>
      {isExpanded && day.top_models?.length > 0 && (
        <tr className="bg-slate-50/50">
          <td colSpan={6} className="py-2 px-4 pl-12">
            <div className="flex flex-wrap gap-2">
              {day.top_models.map(m => (
                <Badge key={m.name} variant="outline" className="text-[10px] font-normal gap-1">
                  {m.name} <span className="font-bold">{m.count}x</span>
                </Badge>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, testId }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  const cls = colorMap[color] || colorMap.slate;
  return (
    <Card className={`border ${cls}`} data-testid={testId}>
      <CardContent className="py-3 px-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4 opacity-60" />
          <span className="text-[11px] font-medium opacity-70">{label}</span>
        </div>
        <p className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
        <p className={`text-[10px] mt-0.5 ${color === 'red' ? 'font-bold' : 'opacity-50'}`}>{sub}</p>
      </CardContent>
    </Card>
  );
}
